/* eslint-disable no-lonely-if */
/* eslint-disable react/jsx-props-no-spreading */
import React, { useContext, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import moment from 'moment-timezone';
import styled from 'styled-components';
import { useMediaQuery } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import {
  PrintFooter,
  PrintHeader,
  Watermark,
  WidgetHeader,
  AgGridWidgetWrapper,
  APIAlert,
} from '@mummssoftware/common-ui';
import {
  GlobalContext,
  SET_FAJITA_FORM,
  SET_FAJITA_WARNING,
} from '@mummssoftware/global-context/GlobalContext';
import { ReqFormSwitch } from './ReqFormSwitch';
import { ReadOnlyFormSwitch } from './ReadOnlyFormSwitch';
import { FajitaContext } from '../FajitaForms/Context';
import { NoteCountCellRenderer } from '../Forms/CellRenderer';
import { FormsTopBar } from './TopBar';
import { Body } from './Body';
import { SubmitPlan } from '../FajitaForms/SubmitPlan';
import { ElectionAddendumPrintDialog } from '../FajitaForms/ElectionAddendumPrintDialog';
import { FormsContext, FormsExtendedPatient } from '../../context';
import { FORM_NAME, questionnaireEngine } from '../Form/constants';
import defaults from '../../translations/en.json';

interface AppProps {
  embedded?: boolean;
}

const AppRoot = styled.div`
  margin-left: var(--safe-area-inset-left);
  margin-right: var(--safe-area-inset-right);
  overflow: hidden;
  display: flex;
  flex-direction: column;
  @media print {
    display: block;
    height: auto;
  }
`;

const FormsContainer = styled.div`
  ${(props: any) => {
    const { getFullSizeHeight } = props.theme.props.dashboard;
    // todo why issue?
    const maxHeight = getFullSizeHeight(window.innerHeight) - 10;
    return `height: ${maxHeight}px;
            max-height: ${maxHeight}px;`;
  }}
  ${(props: any) => {
    const { background, form } = props.theme.palette;
    return props.fajitaSelected
      ? `background-color: ${form.background};}`
      : `background-color: ${background.paper};}`;
  }}
  width: 100%;
  position: relative;
  overflow: auto;
`;

const VisitDiv = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-around;
  align-items: center;
  flex: 1 1 auto;
`;

const useStyles = makeStyles((theme) => ({
  root: {
    backgroundColor: `${(theme as any).palette.success.light}`,
  },
}));

type headerOpts = {
  idgTeam?: string | null;
  idgMeetingDate?: string | null;
  hospiceName: string;
  siteName?: string;
  patient?: FormsExtendedPatient;
};

const createPrintHeaders = ({
  idgTeam,
  idgMeetingDate,
  hospiceName,
  siteName,
  patient,
}: headerOpts) => {
  if (patient) {
    return {
      top: `${idgTeam ? `${idgTeam} - ` : ''}${patient.firstName} ${
        patient.lastName
      } (#${patient.patientNumber})`,
      bottom: `${
        idgMeetingDate
          ? `${moment(idgMeetingDate).format('MM/DD-YYYY')} - `
          : ''
      }${hospiceName} ${siteName}`,
    };
  }
  return {
    top: hospiceName,
    bottom: siteName,
  };
};

const Content = ({ embedded }: AppProps) => {
  const headerRef = useRef<HTMLElement>(null);
  const isNarrowPhoneView = useMediaQuery('(max-width:600px)', {
    noSsr: true,
  });
  const {
    currentUser,
    keycloak,
    patient,
    meetingMetadata: { idgTeam, idgMeetingDate },
    formName,
    appTitle,
    onClick,
    onRemove,
    doNotShow,
    smallWidget,
    patientError,
    savePatientStateToPouch,
    removeReadOnlyForm,
  } = useContext(FormsContext);
  const {
    appVisit,
    levelOfCare,
    fajitaFormGlobal,
    displayFajitaWarning,
    dispatch: globalDispatch,
  } = useContext(GlobalContext);
  const globalContext = useContext(GlobalContext);
  const { formModified, fajitaSelected } = useContext(FajitaContext);
  const isSandBoxForm = fajitaFormGlobal?.issandboxform;
  const showWatermark = isSandBoxForm && formName;
  const fajitaFormName =
    fajitaFormGlobal?.form_name || fajitaFormGlobal?.formname;

  const classes = useStyles();
  const visitText =
    appVisit && `${appVisit.levelofcare} on ${appVisit.effectivedate}`;
  const [{ top, bottom }, setPrintText] = useState({
    top: currentUser.hospiceName,
    bottom: currentUser.siteName,
  });
  const { search } = useLocation();
  const useQuery = () => new URLSearchParams(search);
  const query = useQuery();
  const viewLayout = query.get('viewmode');
  const readOnly: boolean = viewLayout === 'readonly';
  // formName is patient platform
  // localStorage.getItem(FORM_NAME) is cross platform
  const showSubmit = patient ? !!formName : localStorage.getItem(FORM_NAME);
  // cross patient widget => no patient and fajitaFormGlobal
  const showNoteCounter =
    !!fajitaFormGlobal && (fajitaFormGlobal as any)?.id && !patient;

  // ContainerDiv by platform
  const ContainerDiv: any = patient ? FormsContainer : AgGridWidgetWrapper;

  useEffect(() => {
    const { top: newTop, bottom: newBottom } = createPrintHeaders({
      idgTeam,
      idgMeetingDate,
      hospiceName: currentUser.hospiceName,
      siteName: currentUser.siteName,
      patient,
    });
    if (newTop !== top || newBottom !== bottom) {
      setPrintText({
        top: newTop,
        bottom: newBottom,
      });
    }
  }, [
    bottom,
    top,
    currentUser.hospiceName,
    currentUser.siteName,
    idgTeam,
    idgMeetingDate,
    patient,
    setPrintText,
  ]);

  const [showSpinner, setShowSpinner] = useState<boolean>(false);
  const [hideCheckmark, setHideCheckmark] = useState<boolean>(
    smallWidget || false,
  );
  const [doClose, setDoClose] = useState<boolean>(false);

  /**
   * Moved the on click functionality into the following use effects to allow better coordination for async
   * save calls to Pouch to finish before closing the widget.
   */
  const handleOnClick = async () => {
    //! TEMP WARNING FOR SANDBOX FORMS
    globalDispatch({
      type: SET_FAJITA_WARNING,
      payload: false,
    });
    // if NOT FORMIO forms
    if (formName && formName.includes(questionnaireEngine)) {
      // go through the savePatientStateToPouch process useEffect below
      setShowSpinner(true);
      // if FORMIO/FAJITA forms and onClick exists
    } else if (onClick) {
      if (patient) {
        localStorage.removeItem(FORM_NAME);
        globalContext.dispatch({
          type: SET_FAJITA_FORM,
          payload: null,
        });
        removeReadOnlyForm();
        onClick();
        // CrossPlatform widget
      } else {
        // should be async
        if (localStorage.getItem(FORM_NAME)) {
          // open a form and perform no action
          localStorage.removeItem(FORM_NAME);
          // reset Global fajita Form
          globalContext.dispatch({
            type: SET_FAJITA_FORM,
            payload: null,
          });
          removeReadOnlyForm();
        } else {
          // close widget when on Listing
          removeReadOnlyForm();
          onClick();
        }
      }
    }
  };

  useEffect(() => {
    if (showSpinner && savePatientStateToPouch) {
      const awaitSave = async () => {
        try {
          await savePatientStateToPouch();
          setDoClose(true);
        } catch (err) {
          console.log('There was an issue saving the form to Pouch.', err);
        }
      };
      awaitSave();
    }
  }, [savePatientStateToPouch, showSpinner]);

  useEffect(() => {
    if (doClose && onClick) {
      localStorage.removeItem(FORM_NAME);
      setShowSpinner(false);
      onClick();
    }
  }, [doClose, onClick]);

  const widgetNoIcon = patient ? smallWidget || hideCheckmark : false;

  const fullWidgetTitle = fajitaFormName || appTitle || 'Forms';
  const widgetHeaderTitle =
    isNarrowPhoneView && !smallWidget && fajitaFormName ? '' : fullWidgetTitle;

  return (
    <AppRoot id="form-root">
      <React.Fragment>
        <PrintHeader hospiceName={top} siteName={bottom} />
        <FormsTopBar
          ref={headerRef}
          embedded={embedded}
          currentUser={currentUser}
          patient={patient}
          formName={formName}
          logout={keycloak.logout}
        />
      </React.Fragment>

      <WidgetHeader
        onlyCloseWidget={!!patient}
        appTitle={widgetHeaderTitle}
        versionInfo={`Forms v${process.env.REACT_APP_FORMS_VERSION}, SmartChart v${process.env.REACT_APP_VERSION}`}
        onClick={handleOnClick}
        onRemove={onRemove}
        doNotShow={doNotShow}
        smallWidget={smallWidget}
        noIcon={widgetNoIcon}
        disabled={formModified}
        isForm={!!fajitaFormGlobal}
        hasNote={showNoteCounter}
        note={
          showNoteCounter ? (
            <NoteCountCellRenderer data={fajitaFormGlobal} />
          ) : (
            <></>
          )
        }
      >
        <ReadOnlyFormSwitch />
        <ReqFormSwitch />
        {showSubmit && !smallWidget && <SubmitPlan />}
      </WidgetHeader>

      <ContainerDiv smallWidget={smallWidget} fajitaSelected={fajitaSelected}>
        {/* //! TEMP WARNING FOR SANDBOX FORMS */}
        {displayFajitaWarning && (
          <APIAlert
            errorInfo={{
              text: defaults.fajitaFormSandBoxWarning,
              severity: 'error',
            }}
            persistentError
            handleClose={() => {
              globalDispatch({
                type: SET_FAJITA_WARNING,
                payload: false,
              });
            }}
          />
        )}
        {showWatermark ? (
          <Watermark readOnly={false} smallWidget={smallWidget} />
        ) : null}

        <Body
          patientError={patientError}
          formName={formName}
          patient={patient}
          headerRef={headerRef}
          readOnly={readOnly}
          embedded={embedded}
          smallWidget={smallWidget}
          showSpinner={showSpinner}
          hideCheckmark={hideCheckmark}
          setHideCheckmark={setHideCheckmark}
        />

        {patient && <PrintFooter patient={patient} />}
      </ContainerDiv>
      <ElectionAddendumPrintDialog />
    </AppRoot>
  );
};

export default React.memo(Content);
